import { RowsData, valueAmount } from '../types/insights.types'
import { tickFormatterFor } from './formatValue'
import { LegendItem } from './metricView'

// Choropleth ramp from the impact report design, lightest → darkest. A data-viz
// palette like `colourResolver`'s: the Lemonade primary scale goes muted at the
// dark end, so it can't carry the vivid violet the top bucket needs.
export const MAP_BUCKET_COLOURS: readonly string[] = [
  '#E2D7FC',
  '#BFA4F9',
  '#9B71F7',
  '#7940F6',
  '#4B09DD',
]

export const MAP_NO_DATA_COLOUR = '#CCCCCC'

export type MapBucket = { min: number; colour: string }

export const quantileBuckets = (values: readonly number[]): MapBucket[] => {
  const sorted = [...values].sort((a, b) => a - b)
  const count = Math.min(MAP_BUCKET_COLOURS.length, sorted.length)
  const mins = [
    ...new Set(
      Array.from(
        { length: count },
        (_, index) => sorted[Math.floor((index * sorted.length) / count)]
      )
    ),
  ]
  // Fewer buckets take the dark end, so the highest values always read darkest.
  const colours = MAP_BUCKET_COLOURS.slice(
    MAP_BUCKET_COLOURS.length - mins.length
  )

  return mins.map((min, index) => ({ min, colour: colours[index] }))
}

export const bucketColour = (
  buckets: readonly MapBucket[],
  value: number
): string =>
  [...buckets].reverse().find((bucket) => value >= bucket.min)?.colour ??
  MAP_NO_DATA_COLOUR

export const rowsBuckets = (data: RowsData): MapBucket[] =>
  quantileBuckets(data.rows.map((row) => valueAmount(row.value)))

export const mapLegendItems = (
  data: RowsData,
  locale?: string
): LegendItem[] => {
  const format = tickFormatterFor(data.rows[0]?.value, locale)
  const buckets = rowsBuckets(data)

  return buckets.map((bucket, index) => {
    const next = buckets[index + 1]

    return {
      key: `bucket-${index}`,
      label: next
        ? `${format(bucket.min)} - ${format(next.min)}`
        : `${format(bucket.min)} +`,
      color: bucket.colour,
    }
  })
}
